import classNames from "classnames";
import React, { useEffect, useState } from "react";

import { useAppContext } from "../../../hooks/useAppContext";
import { useCart } from "../../../hooks/useCart";
import { useDefinition } from "../../../hooks/useDefinition";
import { useLabels } from "../../../hooks/useLabels";

import { TransparentButton } from "../../transparent-button";

import { useErrorHandling } from "../../../hooks/useErrorHandling";
import { labels } from "./labels";
import { AlternativeIcon } from "../../alternative-icon/alternative-icon";

export const ProductOption = ({
	option,
	isActive,
	onClick,
	optionGroupCode,
	className,
	showPrice,
	icon,
}: {
	option: eXpress.core.IProductOption;
	isActive: boolean;
	onClick: () => void;
	optionGroupCode: string;
	className?: string;
	showPrice?: boolean;
	icon?: eXpress.core.IIcon;
}) => {
	const { createErrorHandler } = useErrorHandling();
	const { client } = useAppContext();
	const cart = useCart();
	const definition = useDefinition();

	const [price, setPrice] = useState<string>();

	const optionSpecificLabel = labels.specificNoOptions(optionGroupCode);

	const [[noNameLabel, specificNoOptionLabel], isLoadingLabels] = useLabels([
		labels.noNameDefault,
		optionSpecificLabel,
	]);

	useEffect(() => {
		if (!cart || !definition) {
			setPrice(undefined);
			return;
		}

		const productVariant = definition.getSelectedProductVariant();

		/*
		 * We don't have a price object in our model,
		 * So we perform a new request to get the price formating rules
		 * Normally this will be a cached request
		 */
		client
			.getPriceAsync(cart, productVariant.code, 0, undefined, undefined)
			.then((p) => {
				// To be sure a cached price object is not changed, we clone it and change the price for the option
				const clonedPrice = p.clone();
				clonedPrice.priceWithDiscount = option.price;
				clonedPrice.priceWithoutDiscount = undefined;
				setPrice(clonedPrice.toString());
			})
			.catch(createErrorHandler());
	}, [createErrorHandler, option, cart, definition, client]);

	const cssClass = classNames([
		{
			"product-option-element": !icon || !icon.svgUrl, // Needed DC SASS icon placement
			"product-option-element--active": isActive,
			"product-option-element--has-subtext": !!price,
		},
		className,
	]);

	if (isLoadingLabels) return null;
	return (
		<TransparentButton
			className="product-option"
			onClick={() => {
				onClick();
			}}
		>
			{!icon?.svgUrl ? (
				<div
					className={cssClass}
					data-code={option.code}
					data-alternative-option-group-code={option.groupCode} // Needed DC SASS icon placement
				>
					<div className="product-options__text">
						<div>{option.name || specificNoOptionLabel || noNameLabel}</div>
						{showPrice && price && <div className="price-text">{price}</div>}
					</div>
				</div>
			) : (
				<div className={cssClass}>
					<div className="product-option-svg-element--has-subtext">
						<AlternativeIcon
							svgUrl={icon.svgUrl}
							color={icon.color}
							overlayColor={icon.overlayColor}
						/>
					</div>
					<div className="product-options__text">
						<div>{option.name || specificNoOptionLabel || noNameLabel}</div>
						{showPrice && price && <div className="price-text">{price}</div>}
					</div>
				</div>
			)}
		</TransparentButton>
	);
};

ProductOption.displayName = "ProductOption";
